<script setup>
import MobileSelect from "mobile-select";
import { twMerge } from "tailwind-merge";
import "~/assets/css/mobile-select.css";

const { t } = useI18n();
const { width } = useWindowSize();

const props = defineProps({
    config: Object,
    type: {
        type: String,
    },
    variant: {
        type: String,
        default: "dark",
    },
    selectId: {
        type: String,
        default: "",
    },
    useIconOnTablet: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["change"]);

const msInstance = ref(null);
const triggerRef = ref(null);
const currentSelectId = ref("");

const model = ref("");
const optionOpen = ref(false);

const onChange = (data) => {
    model.value = data[0];
    emit("change", model.value);
};

const onSelect = (indexArr, instance) => {
    Array.from(instance.sliderList[0].children).forEach((child) => {
        child.classList.remove("selected");
    });
    instance.sliderList[0].children[indexArr[0]].classList.add("selected");
};

const selectItem = (item) => {
    model.value = item;
    optionOpen.value = false;
    emit("change", model.value);
};

const handleClickOutside = (event) => {
    const target = event.target;
    const match = target.closest("[data-select]");

    // 如果點擊的元素不在任何 data-select 區塊內，關閉選單
    if (optionOpen.value && !match) {
        optionOpen.value = false;
        return;
    }

    // 如果點擊的是其他 select 區塊，關閉選單
    if (match) {
        const clickedId = match.dataset.select;
        if (optionOpen.value && currentSelectId.value !== clickedId) {
            optionOpen.value = false;
            return;
        }
    }
};

onMounted(() => {
    document.addEventListener("click", handleClickOutside);
    watchEffect(() => {
        if (props.config.wheels && !msInstance.value && width.value < 1270) {
            msInstance.value = new MobileSelect({
                ...props.config,
                title:
                    props.type === "language"
                        ? t("toolbar.language.name")
                        : props.type === "filter"
                          ? t("toolbar.filter.name")
                          : t("toolbar.sort.name"),
                trigger: triggerRef.value,
                wheels: props.config.wheels,
                onChange,
                onShow: () => {
                    let initValueIndex;

                    if (props.type === "language") {
                        initValueIndex = props.config.wheels[0].data.findIndex(
                            (item) =>
                                item.label ===
                                (model.value?.label || props.config.initValue),
                        );
                    } else {
                        initValueIndex = props.config.wheels[0].data.findIndex(
                            (item) =>
                                item ===
                                (model.value || props.config.initValue),
                        );
                    }

                    msInstance.value.locatePosition(0, initValueIndex);
                    onSelect([initValueIndex], msInstance.value);
                },
                onTransitionEnd: (data, indexArr, instance) =>
                    onSelect(indexArr, instance),
                triggerDisplayValue: false,
                ensureBtnText: t("toolbar.button.confirm"),
                cancelBtnText: t("toolbar.button.cancel"),
                ensureBtnColor: "#ff6400",
                cancelBtnColor:
                    props.variant === "dark"
                        ? "rgba(255, 255, 255, 0.5)"
                        : "rgba(0, 0, 0, 0.4)",
                titleColor: props.variant === "dark" ? "#ffffff" : "#000000",
                textColor: props.variant === "dark" ? "#ffffff" : "#000000",
                bgColor: props.variant === "dark" ? "#353535" : "#e9ecf2",
                titleBgColor: props.variant === "dark" ? "#353535" : "#e9ecf2",
                maskOpacity: 0.6,
            });
            msInstance.value.panel.classList.add(props.variant);
        }
    });
});

defineExpose({
    model: model,
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
    if (msInstance.value) msInstance.value.destroy();
    if (msInstance.value) msInstance.value = null;
});
</script>

<template>
    <!-- Language Button -->
    <div :data-select="selectId" v-if="type === 'language'" ref="triggerRef">
        <SvgLanguage24
            class="h-full w-full lg:hidden"
            :color="variant === 'light' ? '#000' : '#ff6400'"
        />
    </div>
    <button
        :data-select="selectId"
        v-else
        class="relative text-left lg:self-stretch"
    >
        <!-- Filter & Sort Button for Mobile and Tablet (< lg) -->
        <div
            class="slt-sort-button slt-focus-outline flex lg:hidden"
            ref="triggerRef"
            :class="{
                'bg-primary-orange sm:bg-base-deepGrey': variant === 'dark',
                'bg-base-silver sm:bg-base-silver': variant === 'light',
                'use-icon-on-tablet': useIconOnTablet,
            }"
        >
            <span
                class="pointer-events-none absolute right-0 top-0 h-[14px] w-[14px] rounded-full duration-200 ease-in-out"
                :class="
                    twMerge(
                        variant === 'dark'
                            ? 'bg-base-silver'
                            : 'bg-primary-orange',
                        !model || model === props.config.initValue
                            ? 'scale-0'
                            : 'scale-[100%]',
                        useIconOnTablet ? 'lg:scale-0 ' : 'sm:scale-0 ',
                    )
                "
            />
            <SvgFilter24
                v-if="type === 'filter'"
                class="h-[6.1vw] w-[6.1vw] xs:h-[24px] xs:w-[24px]"
                :class="useIconOnTablet ? 'lg:hidden' : 'sm:hidden'"
            />
            <SvgSort24
                v-else-if="type === 'sort'"
                class="h-[6.1vw] w-[6.1vw] xs:h-[24px] xs:w-[24px]"
                :class="useIconOnTablet ? 'lg:hidden' : 'sm:hidden'"
            />
            <p
                class="slt-placeholder hidden flex-1"
                :class="[
                    useIconOnTablet ? 'lg:block' : ' sm:block',
                    {
                        'text-base-white-50': !model && variant === 'dark',
                        'text-base-black-40': !model && variant === 'light',
                        'text-base-white': model && variant === 'dark',
                        'text-base-black': model && variant === 'light',
                    },
                ]"
            >
                {{
                    model
                        ? model
                        : type === "filter"
                          ? t("toolbar.filter.options.newsPage.all")
                          : t("toolbar.sort.options.newsPage.newest")
                }}
            </p>
            <SvgDropDown16
                :color="variant === 'light' ? '#000' : '#ff6400'"
                class="hidden h-4 w-4 transition-transform duration-300 ease-in-out"
                :class="[
                    useIconOnTablet ? 'lg:block' : 'sm:block',
                    {
                        'rotate-180': optionOpen,
                    },
                ]"
            />
            <div
                v-show="model && model !== props.config.initValue"
                class="absolute right-0 top-0 h-[3.56vw] w-[3.56vw] rounded-full bg-base-silver xs:h-[14px] xs:w-[14px] sm:hidden"
                :class="{
                    'bg-base-silver': variant === 'dark',
                    'bg-primary-orange': variant === 'light',
                }"
            ></div>
        </div>
        <!-- Filter & Sort Button for Desktop (>= lg) -->
        <div
            class="slt-sort-button slt-focus-outline hidden lg:flex"
            :class="{
                'bg-base-deepGrey': variant === 'dark',
                'bg-base-silver': variant === 'light',
                'use-icon-on-tablet': useIconOnTablet,
            }"
            @click="(optionOpen = !optionOpen) && (currentSelectId = selectId)"
        >
            <div
                class="slt-placeholder relative w-full overflow-x-hidden whitespace-nowrap after:absolute after:right-0 after:top-0 after:h-full after:w-[30%] after:bg-gradient-to-l after:to-transparent after:content-['']"
                :class="{
                    'after:from-base-deepGrey': variant === 'dark',
                    'after:from-base-silver': variant === 'light',
                    'text-base-white-50': !model && variant === 'dark',
                    'text-base-black-40': !model && variant === 'light',
                    'text-base-white': model && variant === 'dark',
                    'text-base-black': model && variant === 'light',
                }"
            >
                {{
                    model
                        ? model
                        : type === "filter"
                          ? t("toolbar.filter.name")
                          : t("toolbar.sort.name")
                }}
            </div>
            <SvgDropDown16
                :color="variant === 'light' ? '#000' : '#ff6400'"
                class="h-4 w-4 transition-transform duration-300 ease-in-out lg:h-[1.06vw] lg:w-[1.06vw] 3xl:h-[20.3px] 3xl:w-[20.3px]"
                :class="{
                    'rotate-180': optionOpen,
                }"
            />
        </div>
        <transition name="fade">
            <div
                v-show="optionOpen"
                class="absolute left-0 top-[calc(100%+12px)] z-[5] hidden w-full cursor-pointer rounded-[16px] shadow-custom lg:block"
                :class="{
                    'bg-base-deepGrey text-base-white': variant === 'dark',
                    'bg-base-silver': variant === 'light',
                }"
            >
                <ul>
                    <li
                        v-for="(item, index) in config.wheels[0].data"
                        :key="item.value"
                        class="slt-body-1 flex items-center justify-between py-[0.53vw] pl-[1.06vw] pr-[0.53vw] transition-colors duration-150 ease-in-out 3xl:py-[10.2px] 3xl:pl-[20.3px] 3xl:pr-[10.2px]"
                        :class="{
                            'rounded-t-[15px]': index === 0,
                            'rounded-b-[15px]':
                                index === config.wheels[0].data.length - 1,
                            'hover:bg-primary-orange hover:text-base-black':
                                variant === 'dark',
                            'hover:bg-base-blueGrey': variant === 'light',
                            'bg-primary-orange text-base-black':
                                model === item && variant === 'dark',
                            'bg-base-blueGrey':
                                model === item && variant === 'light',
                        }"
                        @click="selectItem(item)"
                    >
                        {{ item }}
                        <SvgSelect24
                            v-if="model === item"
                            class="h-6 w-6 lg:h-[1.59vw] lg:w-[1.59vw] 3xl:h-[30.6px] 3xl:w-[30.6px]"
                            :color="variant === 'dark' ? '#000' : '#FF6400'"
                        />
                    </li>
                </ul>
            </div>
        </transition>
    </button>
</template>

<style scoped>
/* Shared classes */
.slt-sort-button {
    @apply h-[12.21vw] w-[13.23vw] items-center justify-center rounded-[18.32vw] xs:h-[48px] xs:w-[52px] xs:rounded-[72px];
}
/* Write duplicate classes for lg: to prevent unexpected override behavior */
.slt-sort-button:not(.use-icon-on-tablet) {
    @apply sm:w-[24vw] sm:max-w-[184px] sm:rounded-full sm:pl-[2.08vw] sm:pr-[1.56vw] lg:h-full lg:pl-[1.06vw] lg:pr-[0.79vw] 3xl:pl-[20.3px] 3xl:pr-[15.2px];
}
.slt-sort-button.use-icon-on-tablet {
    @apply sm:h-[48px] sm:w-[52px] lg:h-full lg:w-[24vw] lg:max-w-[150px] lg:rounded-full lg:pl-[1.06vw] lg:pr-[0.79vw] 3xl:pl-[20.3px] 3xl:pr-[15.2px];
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.1s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
